import { AccordionProps as MUIAccordionProps } from '@mui/material/Accordion';
import { blue, grey } from '../../theme/colors';
import {
  AccordionContainer,
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
  AccordionItemHeaderProps,
  AccordionTitle,
} from '../atoms/Accordion';
import { SxProps } from '@mui/system';
import { Theme as ThemeMui5 } from '@mui/material/styles/createTheme';
import { FC } from 'react';

export enum AccordionTheme {
  Blue = 'blue',
  White = 'white',
}

type AccordionThemeConfig = {
  bg: string;
  borderColor: string;
  itemBg: string;
};

const themes: Record<AccordionTheme, AccordionThemeConfig> = {
  [AccordionTheme.White]: {
    bg: 'white',
    borderColor: 'white',
    itemBg: grey[100],
  },
  [AccordionTheme.Blue]: {
    bg: blue[50],
    borderColor: blue[50],
    itemBg: 'white',
  },
};

type AccordionItemType = {
  id: number | string;
  header: AccordionItemHeaderProps;
  body?: JSX.Element;
};

type CustomAccordionProps = {
  title?: string;
  color?: string;
  theme?: AccordionTheme;
  items: AccordionItemType[];
  containerSx?: SxProps<ThemeMui5>;
  containerClasses?: string;
  titleSx?: SxProps<ThemeMui5>;
  titleClasses?: string;
  bodySx?: SxProps<ThemeMui5>;
  itemHeaderSx?: SxProps<ThemeMui5>;
  bodyClasses?: string;
};

export type AccordionProps = CustomAccordionProps & Partial<MUIAccordionProps>;

const Accordion: FC<AccordionProps> = ({
  title,
  color = 'primary',
  theme = AccordionTheme.Blue,
  items = [],
  containerSx,
  containerClasses,
  bodySx,
  bodyClasses,
  titleSx,
  titleClasses,
  itemHeaderSx,
  ...restProps
}): JSX.Element => {
  return (
    <AccordionContainer
      elevation={0}
      backgroundColor={themes[theme]?.bg}
      className={containerClasses}
      sx={containerSx}
    >
      {title && (
        <AccordionTitle className={titleClasses} sx={titleSx}>
          {title}
        </AccordionTitle>
      )}
      {items.map(({ id, header, body }, index) => (
        <AccordionItem
          key={`${index}-${id}`}
          {...restProps}
          borderColor={themes[theme]?.borderColor}
        >
          <AccordionItemHeader
            key={`${index}-${id}-header`}
            {...header}
            sx={[header?.sx ?? {}, itemHeaderSx]}
            color={color}
            bg={themes[theme]?.itemBg}
            isExpandable={Boolean(body)}
          />
          {body && (
            <AccordionItemBody className={bodyClasses} sx={bodySx}>
              {body}
            </AccordionItemBody>
          )}
        </AccordionItem>
      ))}
    </AccordionContainer>
  );
};

export default Accordion;
